/**
 * TrustBar - Social Proof & Trust Indicators
 * Tutkimuskeskus 2026 Design System
 *
 * Shows live participation count, time estimate, and anonymity badge
 */

import React from 'react'
import {withStyles} from '@material-ui/core/styles'
import {Box} from '@material-ui/core'

// Professional SVG Icons
const ClockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
)

const ShieldCheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
    </svg>
)

const TrophyIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
        <path d="M4 22h16"/>
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
)

const styles = theme => ({
    trustBar: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px 20px',
        padding: '12px 16px',
        background: 'rgba(32, 90, 64, 0.03)',
        borderBottom: '1px solid rgba(32, 90, 64, 0.06)',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontSize: '0.82rem',
        color: '#4a5d52',
        flexWrap: 'wrap',
        marginBottom: 16,
        [theme.breakpoints.down(600)]: {
            gap: '8px 16px',
            padding: '10px 16px',
            fontSize: '0.78rem',
            marginBottom: 12
        }
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        [theme.breakpoints.down(600)]: {
            gap: 5
        }
    },
    liveChip: {
        fontWeight: 600,
        color: '#205a40'
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: '#10b981',
        boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
        animation: '$pulse 2s ease-in-out infinite'
    },
    '@keyframes pulse': {
        '0%, 100%': {
            boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
            opacity: 1
        },
        '50%': {
            boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.1)',
            opacity: 0.8
        }
    },
    separator: {
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: 'rgba(32, 90, 64, 0.15)',
        [theme.breakpoints.down(600)]: {
            display: 'none'
        }
    },
    icon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#5a7a6a',
        opacity: 0.85
    },
    shieldIcon: {
        color: '#205a40'
    },
    winnerChip: {
        fontWeight: 500,
        color: '#1a2e23',
        flexWrap: 'wrap',
        justifyContent: 'center'
    },
    winnerIcon: {
        color: '#d4a340'
    },
    winnerLabel: {
        color: '#6a7d72',
        fontWeight: 400,
    },
    winnerName: {
        fontWeight: 600,
        color: '#1a2e23'
    },
    winnerPrize: {
        color: '#205a40',
        fontWeight: 500
    }
})

const TrustBar = ({
                      classes,
                      cityName = 'Turku',
                      cityCount = 156,
                      estimatedTime = '2 min',
                      showAnonymous = true,
                      trustText = null,
                      latestWinner = null
                  }) => {
    // Format winner name: "Katariina B. (Uusimaa)"
    const formatWinnerDisplay = (winner) => {
        if (!winner || !winner.winner_name) return null
        return `${winner.winner_name}`
    }

    const winnerDisplay = formatWinnerDisplay(latestWinner)

    return (
        <Box className={classes.trustBar}>
            {/* Live participation indicator */}
            {trustText && (
                <>
                    <span className={`${classes.chip} ${classes.liveChip}`}>
                        <span className={classes.liveDot}/>
                        {trustText}
                    </span>
                    <span className={classes.separator}/>
                </>
            )}

            {/* Latest winner */}
            {latestWinner && (
                <>
                    <span className={`${classes.chip} ${classes.winnerChip}`}>
                        <span className={`${classes.icon} ${classes.winnerIcon}`}>
                            <TrophyIcon/>
                        </span>
                        <span className={classes.winnerLabel}>Edeltävä onnekkain:</span>
                        <span className={classes.winnerName}>{winnerDisplay}</span>
                        {latestWinner.price && (
                            <span className={classes.winnerPrize}>– {latestWinner.price}</span>
                        )}
                    </span>
                </>
            )}

            {/* Time estimate */}
            {!latestWinner &&
                <>
                    {trustText && <span className={classes.separator}/>}
                    <span className={classes.chip}>
                    <span className={classes.icon}>
                        <ClockIcon/>
                    </span>
                    ~{estimatedTime}
                    </span>
                </>}

            {/* Anonymous/Secure badge */}
            {!latestWinner && showAnonymous && (
                <>
                    <span className={classes.separator}/>
                    <span className={classes.chip}>
                        <span className={`${classes.icon} ${classes.shieldIcon}`}>
                            <ShieldCheckIcon/>
                        </span>
                        Anonyymi
                    </span>
                </>
            )}
        </Box>
    )
}

export default withStyles(styles)(TrustBar)
